import { useState } from 'react'
import { Plus, X } from 'lucide-react'
import { cn } from '@/utils/cn'
import type { BlocoLabel } from '@/types/bloco'
import { BlocoLabelChip } from './BlocoLabelChip'
import { DEFAULT_LABEL_COLORS } from './blocosKanbanUtils'

interface BlocoLabelPickerProps {
  labels: BlocoLabel[]
  selectedIds: string[]
  saving?: boolean
  onToggle: (labelId: string) => void
  onCreate: (name: string, color: string) => Promise<unknown>
  onDelete?: (labelId: string) => Promise<unknown>
  compact?: boolean
}

export function BlocoLabelPicker({
  labels,
  selectedIds,
  saving = false,
  onToggle,
  onCreate,
  onDelete,
  compact = false,
}: BlocoLabelPickerProps) {
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [color, setColor] = useState(DEFAULT_LABEL_COLORS[0])

  const handleCreate = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    await onCreate(trimmed, color)
    setName('')
    setCreating(false)
  }

  return (
    <div className={cn('space-y-2', compact && 'space-y-1')}>
      <div className='flex flex-wrap gap-1.5'>
        {labels.map((label) => {
          const active = selectedIds.includes(label.id)
          return (
            <div key={label.id} className='inline-flex items-center gap-0.5'>
              <button
                type='button'
                disabled={saving}
                onClick={() => onToggle(label.id)}
                className='transition-opacity hover:opacity-90 disabled:opacity-50'
              >
                <BlocoLabelChip label={label} active={active} />
              </button>
              {onDelete && (
                <button
                  type='button'
                  disabled={saving}
                  onClick={() => void onDelete(label.id)}
                  className='rounded p-0.5 text-text-muted hover:bg-red-500/10 hover:text-red-500'
                  aria-label={`Remover etiqueta ${label.name}`}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          )
        })}
        <button
          type='button'
          onClick={() => setCreating((v) => !v)}
          className='inline-flex items-center gap-1 rounded-full border border-dashed border-[var(--glass-border)] px-2 py-0.5 text-[11px] text-text-muted hover:text-text-primary'
        >
          <Plus size={10} />
          Etiqueta
        </button>
      </div>

      {creating && (
        <div className='flex flex-wrap items-center gap-2 rounded-lg border border-[var(--glass-border)] bg-bg-card p-2'>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Nome da etiqueta'
            maxLength={50}
            className='min-w-[120px] flex-1 rounded border border-[var(--glass-border)] bg-transparent px-2 py-1 text-xs'
          />
          <div className='flex items-center gap-2'>
            <span className='text-[11px] text-text-muted'>Cor</span>
            <div className='flex gap-1'>
              {DEFAULT_LABEL_COLORS.map((c) => (
                <button
                  key={c}
                  type='button'
                  onClick={() => setColor(c)}
                  className={cn(
                    'h-5 w-5 rounded-full border-2',
                    color === c ? 'border-text-primary' : 'border-transparent',
                  )}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <button
            type='button'
            disabled={saving || !name.trim()}
            onClick={() => void handleCreate()}
            className='rounded bg-[var(--brand-primary)] px-2 py-1 text-xs font-medium text-white disabled:opacity-50'
          >
            Criar
          </button>
        </div>
      )}
    </div>
  )
}
